import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import MainButton from '../../../shared/layout/buttons/MainButton';
import StandardModal from '../../../shared/layout/modals/StandardModal';
import AdminFilterBar from './components/AdminFilterBar';
import AdminDataTable from './components/AdminDataTable';
import FreshnessBadge from './components/FreshnessBadge';
import AdminIntegrationsSubnav from './components/AdminIntegrationsSubnav';
import {
  fetchAdminIntegrationWebhooks,
  testAdminIntegrationWebhook,
  enableAdminIntegrationWebhook,
  disableAdminIntegrationWebhook,
} from '../../../services/Http/AdminApi';
import { SimpleSelect } from '../../../shared/layout/forms/AppSelect';

const AdminWebhookHealth = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchStatus, setFetchStatus] = useState('running');
  const loadRequest = useRef(0);
  const [isActive, setIsActive] = useState('');
  const [actionState, setActionState] = useState({ open: false, row: null, action: null, busy: false });

  const load = useCallback(async () => {
    const request = ++loadRequest.current;
    setFetchStatus('running');
    setLoading(true);
    try {
      const resp = await fetchAdminIntegrationWebhooks({ is_active: isActive });
      if (request !== loadRequest.current) return;
      setRows(resp?.data || []);
      setFetchStatus('success');
    } catch (error) {
      if (request !== loadRequest.current) return;
      setFetchStatus('failed');
      toast.error(error?.response?.data?.message || 'Failed to load webhook health');
    } finally {
      if (request === loadRequest.current) setLoading(false);
    }
  }, [isActive]);

  useEffect(() => { load(); return () => { loadRequest.current += 1; }; }, [load]);

  const openAction = (row, action) => setActionState({ open: true, row, action, busy: false });
  const closeAction = () => setActionState({ open: false, row: null, action: null, busy: false });

  const performAction = async () => {
    const { row, action } = actionState;
    if (!row || !action) return;
    setActionState((s) => ({ ...s, busy: true }));
    try {
      if (action === 'test') await testAdminIntegrationWebhook(row.id);
      if (action === 'enable') await enableAdminIntegrationWebhook(row.id);
      if (action === 'disable') await disableAdminIntegrationWebhook(row.id);
      toast.success(`Webhook ${action} completed`);
      closeAction();
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.message || `Webhook ${action} failed`);
      setActionState((s) => ({ ...s, busy: false }));
    }
  };

  const columns = useMemo(() => ([
    { key: 'integrator_name', label: 'Integrator' },
    { key: 'organization_name', label: 'Organization' },
    { key: 'url', label: 'URL', render: (r) => <CodeEllipsis title={r.url}>{r.url || '—'}</CodeEllipsis> },
    { key: 'last_delivery_status', label: 'Last Status' },
    { key: 'failure_count', label: 'Failure Count' },
    { key: 'is_active', label: 'Active', render: (r) => (r.is_active ? 'Yes' : 'No') },
    { key: 'last_delivery_at', label: 'Last Delivery', render: (r) => (r.last_delivery_at ? new Date(r.last_delivery_at).toLocaleString() : '—') },
    {
      key: 'actions',
      label: 'Actions',
      render: (r) => (
        <Actions>
          <InlineBtn type="button" onClick={() => openAction(r, 'test')}>Test</InlineBtn>
          {r.is_active
            ? <InlineBtn type="button" onClick={() => openAction(r, 'disable')}>Disable</InlineBtn>
            : <InlineBtn type="button" onClick={() => openAction(r, 'enable')}>Enable</InlineBtn>}
        </Actions>
      ),
    },
  ]), []);

  return (
    <PageContainer>
      <PageHeader><PageTitle>Webhook Health</PageTitle></PageHeader>
      <PageContent>
        <AdminIntegrationsSubnav />
        <AdminFilterBar>
          <Row>
            <Select value={isActive} onChange={(e) => setIsActive(e.target.value)}>
              <option value="">Any status</option>
              <option value="true">Active only</option>
              <option value="false">Inactive only</option>
            </Select>
            <FreshnessBadge status={fetchStatus} sourceType="operational" />
          </Row>
          <MainButton size="small" onClick={load}>Refresh</MainButton>
        </AdminFilterBar>

        {fetchStatus === 'failed' ? <p role="alert">Failed to load webhook health</p> : <AdminDataTable columns={columns} rows={rows} loading={loading} emptyText="No webhooks found" />}

        <StandardModal
          isOpen={actionState.open}
          onClose={closeAction}
          title="Confirm Webhook Action"
          footer={(
            <>
              <MainButton variant="secondary" onClick={closeAction} disabled={actionState.busy}>Cancel</MainButton>
              <MainButton onClick={performAction} disabled={actionState.busy}>{actionState.busy ? 'Working...' : 'Confirm'}</MainButton>
            </>
          )}
        >
          <p>
            {actionState.action ? `Run '${actionState.action}' for webhook on integrator '${actionState.row?.integrator_name || 'Unknown'}'?` : 'Confirm action'}
          </p>
          <CodeEllipsis as="div">{actionState.row?.url || '—'}</CodeEllipsis>
          {actionState.action !== 'test' && <p style={{ marginTop: 10, fontSize: 12, opacity: 0.9 }}>This action creates an admin audit log entry.</p>}
        </StandardModal>
      </PageContent>
    </PageContainer>
  );
};

export default AdminWebhookHealth;

const Row = styled.div`display:flex;gap: 0.625rem;align-items:center;flex-wrap:wrap;`;
const Select = styled(SimpleSelect)`border:1px solid ${({theme})=>theme.colors.border};border-radius:10px;padding: 0.5625rem 0.75rem;background:${({theme})=>theme.colors.surface};`;
const Actions = styled.div`display:flex;gap: 0.5rem;flex-wrap:wrap;`;
const InlineBtn = styled.button`
  border:1px solid ${({theme})=>theme.colors.border}; background:${({theme})=>theme.colors.surface}; color:${({theme})=>theme.colors.textPrimary};
  border-radius:8px; padding: 0.375rem 0.625rem; cursor:pointer; font-weight:600;
  &:hover { border-color: var(--color-primary); color: var(--color-primary); }
`;
const CodeEllipsis = styled.code`
  display: inline-block;
  max-width: 380px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.75rem;
`;
